import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import ProService from '../../services/ProService';

const STAGES = [
  { id: 'new', label: 'New', color: '#3B82F6' },
  { id: 'contacted', label: 'Contacted', color: '#8B5CF6' },
  { id: 'qualified', label: 'Qualified', color: '#F59E0B' },
  { id: 'proposal', label: 'Proposal', color: '#EC4899' },
  { id: 'negotiating', label: 'Negotiating', color: '#6366F1' },
  { id: 'won', label: 'Won', color: '#10B981' },
  { id: 'lost', label: 'Lost', color: '#EF4444' },
];

const SOURCES = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google', label: 'Google' },
  { value: 'cold_outreach', label: 'Cold Outreach' },
  { value: 'other', label: 'Other' },
];

export default function PipelinePage() {
  const { currentUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [view, setView] = useState('kanban'); // kanban or list
  const [draggedLead, setDraggedLead] = useState(null);

  // Fetch leads with real-time updates
  useEffect(() => {
    if (!currentUser?.uid) return;

    const leadsRef = collection(db, 'users', currentUser.uid, 'leads');
    const q = query(leadsRef, orderBy('updatedAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leadsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLeads(leadsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Fetch analytics
  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchAnalytics = async () => {
      try {
        const data = await ProService.getLeadAnalytics(currentUser.uid);
        setAnalytics(data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      }
    };

    fetchAnalytics();
  }, [currentUser?.uid]);

  // Group leads by stage
  const pipelineData = STAGES.reduce((acc, stage) => {
    acc[stage.id] = leads.filter(lead => lead.stage === stage.id);
    return acc;
  }, {});

  // Calculate pipeline value
  const pipelineValue = leads
    .filter(l => !['won', 'lost'].includes(l.stage))
    .reduce((sum, l) => sum + (l.value || 0), 0);

  // Handle drag and drop
  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedLead || draggedLead.stage === targetStage) {
      setDraggedLead(null);
      return;
    }

    try {
      const leadRef = doc(db, 'users', currentUser.uid, 'leads', draggedLead.id);
      await updateDoc(leadRef, {
        stage: targetStage,
        updatedAt: serverTimestamp(),
      });

      // Add activity
      const activitiesRef = collection(leadRef, 'activities');
      await addDoc(activitiesRef, {
        type: 'stage_change',
        content: `Stage changed from ${draggedLead.stage} to ${targetStage}`,
        createdAt: serverTimestamp(),
        metadata: { from: draggedLead.stage, to: targetStage },
      });

      toast.success(`Moved to ${STAGES.find(s => s.id === targetStage)?.label}`);
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Failed to update stage');
    }

    setDraggedLead(null);
  };

  const getTemperatureColor = (temp) => {
    switch (temp) {
      case 'hot': return 'bg-red-500';
      case 'warm': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-[#0A0A0A]">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-[#1A1A1A] p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Pipeline</h1>
            <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">
              Manage your leads and track conversions
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-[#1A1A1A] rounded-lg p-1">
              <button
                onClick={() => setView('kanban')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === 'kanban'
                    ? 'bg-white dark:bg-[#2A2A2A] text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-[#666666]'
                }`}
              >
                Board
              </button>
              <button
                onClick={() => setView('list')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === 'list'
                    ? 'bg-white dark:bg-[#2A2A2A] text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-[#666666]'
                }`}
              >
                List
              </button>
            </div>
            {/* Add Lead Button */}
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-brand text-white text-sm font-semibold rounded-lg hover:bg-brand-hover transition-colors"
            >
              Add Lead
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex items-center gap-6 mt-6">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {leads.length}
            </span>
            <span className="text-sm text-gray-500 dark:text-[#666666]">Total Leads</span>
          </div>
          <div className="w-px h-8 bg-gray-200 dark:bg-[#2A2A2A]" />
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-brand">
              ${pipelineValue.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500 dark:text-[#666666]">Pipeline Value</span>
          </div>
          <div className="w-px h-8 bg-gray-200 dark:bg-[#2A2A2A]" />
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-green-500">
              {leads.filter(l => l.temperature === 'hot').length}
            </span>
            <span className="text-sm text-gray-500 dark:text-[#666666]">Hot Leads</span>
          </div>
          {analytics && (
            <>
              <div className="w-px h-8 bg-gray-200 dark:bg-[#2A2A2A]" />
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {analytics.conversionRate}%
                </span>
                <span className="text-sm text-gray-500 dark:text-[#666666]">Conversion</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      {view === 'kanban' && (
        <div className="flex-1 overflow-x-auto p-6">
          <div className="flex gap-4 h-full min-w-max">
            {STAGES.map((stage) => (
              <div
                key={stage.id}
                className="w-72 flex-shrink-0 flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {stage.label}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-[#666666] bg-gray-100 dark:bg-[#1A1A1A] px-2 py-0.5 rounded-full">
                      {pipelineData[stage.id]?.length || 0}
                    </span>
                  </div>
                </div>

                {/* Column Content */}
                <div className="flex-1 space-y-2 overflow-y-auto pb-4">
                  <AnimatePresence>
                    {pipelineData[stage.id]?.map((lead) => (
                      <motion.div
                        key={lead.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead)}
                        onClick={() => setSelectedLead(lead)}
                        className={`p-4 bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] cursor-pointer hover:border-brand/50 transition-all group ${
                          draggedLead?.id === lead.id ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600 dark:text-[#999999]">
                                {lead.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                                {lead.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-[#666666] line-clamp-1">
                                {lead.email}
                              </p>
                            </div>
                          </div>
                          {lead.temperature && (
                            <div className={`w-2 h-2 rounded-full ${getTemperatureColor(lead.temperature)}`} />
                          )}
                        </div>

                        {lead.projectType && (
                          <p className="text-xs text-gray-500 dark:text-[#666666] mb-2">
                            {lead.projectType}
                          </p>
                        )}

                        <div className="flex items-center justify-between">
                          {lead.value > 0 && (
                            <span className="text-sm font-semibold text-brand">
                              ${lead.value.toLocaleString()}
                            </span>
                          )}
                          {lead.score !== undefined && (
                            <div className="flex items-center gap-1">
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-[#2A2A2A] rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    lead.score >= 70 ? 'bg-red-500' :
                                    lead.score >= 40 ? 'bg-yellow-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${lead.score}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-[#666666]">
                                {lead.score}
                              </span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {pipelineData[stage.id]?.length === 0 && (
                    <div className="h-24 flex items-center justify-center border-2 border-dashed border-gray-200 dark:border-[#2A2A2A] rounded-xl">
                      <p className="text-sm text-gray-400 dark:text-[#666666]">Drop here</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white dark:bg-[#111111] rounded-xl border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-[#0A0A0A]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wider">
                    Lead
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#666666] uppercase tracking-wider">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-[#1A1A1A]">
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className="hover:bg-gray-50 dark:hover:bg-[#1A1A1A] cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600 dark:text-[#999999]">
                            {lead.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.name}</p>
                          <p className="text-xs text-gray-500 dark:text-[#666666]">{lead.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full"
                        style={{
                          backgroundColor: `${STAGES.find(s => s.id === lead.stage)?.color}20`,
                          color: STAGES.find(s => s.id === lead.stage)?.color,
                        }}
                      >
                        {STAGES.find(s => s.id === lead.stage)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-brand">
                      ${(lead.value || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getTemperatureColor(lead.temperature)}`} />
                        <span className="text-sm text-gray-900 dark:text-white">{lead.score || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-[#666666]">
                      {SOURCES.find(s => s.value === lead.source)?.label || lead.source}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        userId={currentUser?.uid}
      />

      {/* Lead Detail Modal */}
      <LeadDetailModal
        lead={selectedLead}
        isOpen={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        userId={currentUser?.uid}
      />

      {/* Loading State */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-black/50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

// Add Lead Modal Component
function AddLeadModal({ isOpen, onClose, userId }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'website',
    projectType: '',
    value: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const leadsRef = collection(db, 'users', userId, 'leads');
      await addDoc(leadsRef, {
        ...formData,
        value: parseFloat(formData.value) || 0,
        stage: 'new',
        score: 0,
        temperature: 'cold',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success('Lead added successfully');
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        source: 'website',
        projectType: '',
        value: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error adding lead:', error);
      toast.error('Failed to add lead');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-[#111111] rounded-2xl w-full max-w-lg mx-4 p-6 border border-gray-200 dark:border-[#1A1A1A]"
      >
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Add New Lead</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand transition-colors"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand transition-colors"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand transition-colors"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
                Company
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand transition-colors"
                placeholder="Company name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
                Source
              </label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-brand transition-colors"
              >
                {SOURCES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
                Estimated Value
              </label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand transition-colors"
                placeholder="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
              Project Type
            </label>
            <input
              type="text"
              value={formData.projectType}
              onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand transition-colors"
              placeholder="Wedding, Portrait, Event..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand transition-colors resize-none"
              placeholder="Any additional notes..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-gray-700 dark:text-white text-sm font-medium hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-brand text-white text-sm font-semibold rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
            >
              {saving ? 'Adding...' : 'Add Lead'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Lead Detail Modal Component
function LeadDetailModal({ lead, isOpen, onClose, userId }) {
  const [activeTab, setActiveTab] = useState('details');
  const [activities, setActivities] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!lead?.id || !userId) return;

    const activitiesRef = collection(db, 'users', userId, 'leads', lead.id, 'activities');
    const q = query(activitiesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [lead?.id, userId]);

  useEffect(() => {
    if (!lead?.id || !userId) return;

    const fetchSuggestions = async () => {
      try {
        const data = await ProService.getSmartSuggestions(userId, lead.id);
        setSuggestions(data.suggestions || []);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
      }
    };

    fetchSuggestions();
  }, [lead?.id, userId]);

  const addNote = async () => {
    if (!newNote.trim()) return;

    setSaving(true);
    try {
      const activitiesRef = collection(db, 'users', userId, 'leads', lead.id, 'activities');
      await addDoc(activitiesRef, {
        type: 'note',
        content: newNote,
        createdAt: serverTimestamp(),
      });
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const updateLead = async (updates) => {
    try {
      const leadRef = doc(db, 'users', userId, 'leads', lead.id);
      await updateDoc(leadRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      toast.success('Lead updated');
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-[#111111] rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden border border-gray-200 dark:border-[#1A1A1A]"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-[#1A1A1A]">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center">
                <span className="text-lg font-medium text-gray-600 dark:text-[#999999]">
                  {lead.name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{lead.name}</h2>
                <p className="text-sm text-gray-500 dark:text-[#666666]">{lead.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-[#1A1A1A] rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Quick Stats */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <span
                className="px-2.5 py-1 text-xs font-medium rounded-full"
                style={{
                  backgroundColor: `${STAGES.find(s => s.id === lead.stage)?.color}20`,
                  color: STAGES.find(s => s.id === lead.stage)?.color,
                }}
              >
                {STAGES.find(s => s.id === lead.stage)?.label}
              </span>
            </div>
            {lead.value > 0 && (
              <span className="text-sm font-semibold text-brand">
                ${lead.value.toLocaleString()}
              </span>
            )}
            {lead.score !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-[#666666]">Score:</span>
                <span className={`text-sm font-medium ${
                  lead.score >= 70 ? 'text-red-500' :
                  lead.score >= 40 ? 'text-yellow-500' : 'text-blue-500'
                }`}>
                  {lead.score}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-[#1A1A1A]">
          {['details', 'activity', 'suggestions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-brand border-b-2 border-brand'
                  : 'text-gray-500 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {activeTab === 'details' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-[#666666] mb-1">Phone</label>
                  <p className="text-sm text-gray-900 dark:text-white">{lead.phone || '—'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-[#666666] mb-1">Company</label>
                  <p className="text-sm text-gray-900 dark:text-white">{lead.company || '—'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-[#666666] mb-1">Source</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {SOURCES.find(s => s.value === lead.source)?.label || lead.source || '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-[#666666] mb-1">Project Type</label>
                  <p className="text-sm text-gray-900 dark:text-white">{lead.projectType || '—'}</p>
                </div>
              </div>

              {lead.notes && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-[#666666] mb-1">Notes</label>
                  <p className="text-sm text-gray-900 dark:text-white">{lead.notes}</p>
                </div>
              )}

              {/* Qualification Checkboxes */}
              <div className="pt-4 border-t border-gray-200 dark:border-[#1A1A1A]">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Qualification</p>
                <div className="space-y-2">
                  {[
                    { key: 'hasResponded', label: 'Has Responded' },
                    { key: 'hasBudget', label: 'Budget Confirmed' },
                    { key: 'hasDate', label: 'Date Confirmed' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={lead[key] || false}
                        onChange={(e) => updateLead({ [key]: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 dark:border-[#2A2A2A] text-brand focus:ring-brand"
                      />
                      <span className="text-sm text-gray-700 dark:text-[#999999]">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              {/* Add Note */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addNote()}
                  placeholder="Add a note..."
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-[#0A0A0A] border border-gray-200 dark:border-[#2A2A2A] rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:outline-none focus:border-brand"
                />
                <button
                  onClick={addNote}
                  disabled={saving || !newNote.trim()}
                  className="px-4 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
                >
                  Add
                </button>
              </div>

              {/* Activity List */}
              <div className="space-y-3">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex gap-3 p-3 bg-gray-50 dark:bg-[#0A0A0A] rounded-lg"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                      {activity.type === 'note' && (
                        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      )}
                      {activity.type === 'stage_change' && (
                        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white">{activity.content}</p>
                      <p className="text-xs text-gray-500 dark:text-[#666666] mt-1">
                        {activity.createdAt?.toDate?.()?.toLocaleDateString() || 'Just now'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'suggestions' && (
            <div className="space-y-3">
              {suggestions.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-[#666666] text-center py-8">
                  No suggestions available
                </p>
              ) : (
                suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${
                      suggestion.priority === 'high'
                        ? 'border-l-red-500 bg-red-50 dark:bg-red-500/10'
                        : suggestion.priority === 'medium'
                        ? 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-500/10'
                        : 'border-l-gray-400 bg-gray-50 dark:bg-[#1A1A1A]'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {suggestion.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-[#666666] mt-1">
                      {suggestion.description}
                    </p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
